import { ChangeDetectorRef, Component, DestroyRef, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { catchError, combineLatest, distinctUntilChanged, map, of, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CfiMatTableComponent, ColumnDefinition } from '@k12/tables';
import { ToastMessageService } from '@k12/app-services';
import { ToastMessageType } from '@k12/models';
import { AwardsService } from '../../../awards/awards.service';
import { StudentLedgerTransaction } from '../../../awards/models/student-ledger-transaction.model';

export interface StudentTransaction {
  distributionDate: Date | null;
  academicYear: string;
  school: string;
  program: string;
  term: string;
  distributedAmount: number;
}

@Component({
  selector: 'lib-student-ledger-detail',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, CurrencyPipe, DatePipe, CfiMatTableComponent],
  templateUrl: './student-ledger-detail.html',
  styleUrls: ['./student-ledger-detail.scss'],
})
export class StudentLedgerDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly awardsService = inject(AwardsService);
  private readonly toastMessageService = inject(ToastMessageService);

  transactions: StudentTransaction[] = [];

  readonly columnDefinitions: ColumnDefinition[] = [
    { name: 'distributionDate', header: 'Distribution Date', sortable: true, width: 13 },
    { name: 'academicYear', header: 'Academic Year', sortable: true, width: 13 },
    { name: 'school', header: 'School', sortable: true, width: 20 },
    { name: 'program', header: 'Program', sortable: true, width: 13 },
    { name: 'term', header: 'Term', sortable: true, width: 11 },
    { name: 'distributedAmount', header: 'Distributed Amount', sortable: true, width: 12 },
  ];

  readonly displayedColumns: string[] = this.columnDefinitions.map(col => col.name);

  public ngOnInit(): void {
    const studentId$ = combineLatest(this.route.pathFromRoot.map((route) => route.paramMap)).pipe(
      map((paramMaps): string => {
        for (let i = paramMaps.length - 1; i >= 0; i--) {
          const studentId = paramMaps[i].get('studentId');
          if (studentId) {
            return studentId;
          }
        }

        return '';
      }),
      distinctUntilChanged()
    );

    const academicYearId$ = this.route.queryParamMap.pipe(
      map((queryParams) => this.parseAcademicYearId(queryParams.get('academicYearId'))),
      distinctUntilChanged()
    );

    combineLatest([studentId$, academicYearId$])
      .pipe(
        switchMap(([studentId, academicYearId]: [string, number | null]) => {
          if (!studentId) {
            return of([] as StudentLedgerTransaction[]);
          }

          return this.awardsService.getStudentLedger(studentId, academicYearId).pipe(
            catchError((error: HttpErrorResponse) => {
              const errorMessage = error?.error?.message || error?.message || '';
              if (errorMessage) {
                this.toastMessageService.showToast(ToastMessageType.Error, errorMessage);
              }
              return of([] as StudentLedgerTransaction[]);
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((transactions: StudentLedgerTransaction[]) => {
        this.transactions = (transactions ?? []).map((transaction) => this.mapTransaction(transaction));
        this.cdr.detectChanges();
      });
  }

  private mapTransaction(transaction: StudentLedgerTransaction): StudentTransaction {
    return {
      distributionDate: transaction.distributionDate ? new Date(transaction.distributionDate) : null,
      academicYear: transaction.academicYear ?? '',
      school: transaction.school ?? '',
      program: transaction.program ?? '',
      term: transaction.term ?? '',
      distributedAmount: transaction.distributedAmount ?? 0
    };
  }

  private parseAcademicYearId(value: string | null): number | null {
    if (!value) {
      return null;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
}
